import { backendApi } from "./backend.api";
import {
  IProduct,
  IRestHistoryProduct,
  IRestProduct,
} from "shared/models/models";
import { IListPagination } from "shared/utils/pagination";
import { getSpaceId } from "shared/utils/space";

export type IRestHistoryQuery = {
  id: number;
  dateStart?: string;
  dateEnd?: string;
  page?: number;
  type?: "INCOME" | "OUTCOME";
};

export type IRestQuery = {
  id: number;
  addressId?: number;
  dateStart?: string;
  dateEnd?: string;
};

export interface IProductListQuery {
  available?: boolean;
  category?: number;
  set?: boolean;
  snack?: boolean;
  title?: string;
  spaceId?: number;
}

const baseUrl = "/product";
export const productApi = backendApi.injectEndpoints({
  endpoints: (build) => {
    return {
      products: build.query<IProduct[], IProductListQuery>({
        query: (queryParams) => {
          const spaceId = getSpaceId();
          const params: IProductListQuery = { ...queryParams };
          if (spaceId) {
            params.spaceId = spaceId;
          }
          return {
            url: baseUrl,
            method: "GET",
            params: params,
          };
        },
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: "Products" as const, id })),
                "Products",
              ]
            : ["Products"],
      }),
      getProduct: build.query<IProduct, number>({
        query: (id) => ({ url: `${baseUrl}/${id}`, method: "GET" }),
        transformErrorResponse: (response: { status: string | number }) =>
          response.status,
        providesTags: (item) => [{ type: "Products", id: item?.id }],
      }),
      getRestProduct: build.query<IRestProduct, IRestQuery>({
        query: (query) => {
          const params = { ...query };
          if (params.dateEnd) {
            const date = new Date(params.dateEnd);
            date.setDate(date.getDate() + 1);
            date.setUTCHours(21, 0, 0, 0);
            params.dateEnd = date.toISOString();
          }
          return {
            url: `${baseUrl}/rest/${query.id}`,
            method: "GET",
            params: { ...params },
          };
        },
        transformErrorResponse: (response: { status: string | number }) =>
          response.status,
        providesTags: (item) => [{ type: "RestProducts", id: item?.id }],
      }),
      getRestHistoryProduct: build.query<
        IListPagination<IRestHistoryProduct>,
        IRestHistoryQuery
      >({
        query: (queryParams) => {
          const params = { ...queryParams };
          if (params.dateEnd) {
            const date = new Date(params.dateEnd);
            date.setDate(date.getDate() + 1);
            date.setUTCHours(21, 0, 0, 0);
            params.dateEnd = date.toISOString();
          }
          return {
            url: `${baseUrl}/rest-history/${queryParams.id}`,
            method: "GET",
            params: { ...params },
          };
        },
        transformErrorResponse: (response: { status: string | number }) =>
          response.status,
        providesTags: (result) =>
          result
            ? [
                ...result.data.map(({ id }) => ({
                  type: "RestHistoryProduct" as const,
                  id,
                })),
                "RestHistoryProduct",
              ]
            : ["RestHistoryProduct"],
      }),
      addProduct: build.mutation<IProduct, Partial<IProduct>>({
        query: (payload) => {
          const spaceId = getSpaceId();
          const params: Partial<IProduct> = { ...payload };
          if (spaceId) {
            params.spaceId = spaceId;
          }
          return {
            url: baseUrl,
            method: "POST",
            data: params,
          };
        },
        invalidatesTags: ["Products"],
        transformResponse: (response: { data: IProduct }) => response.data,
      }),
      updateProduct: build.mutation<IProduct, Partial<IProduct>>({
        query: (payload) => ({
          url: `${baseUrl}/${payload.id}`,
          method: "PATCH",
          data: payload,
        }),
        invalidatesTags: (item) => [{ type: "Products", id: item?.id }],
      }),
      updateMultiProducts: build.mutation<IProduct[], IProduct[]>({
        query: (payload) => ({
          url: `${baseUrl}/update-multi`,
          method: "POST",
          data: payload,
        }),
        invalidatesTags: (result = []) => [
          ...result.map(({ id }) => ({ type: "Products", id: id }) as const),
        ],
        // transformResponse: (response: { data: IVisit[] }) => response.data,
      }),
      deleteProduct: build.mutation<IProduct, number>({
        query: (id) => ({
          url: `${baseUrl}/${id}`,
          method: "DELETE",
          credentials: "include",
        }),
        invalidatesTags: ["Products"],
      }),
    };
  },
});

export const {
  useProductsQuery,
  useAddProductMutation,
  useDeleteProductMutation,
  useGetProductQuery,
  useUpdateProductMutation,
  useUpdateMultiProductsMutation,
  useGetRestProductQuery,
  useGetRestHistoryProductQuery,
} = productApi;
